<template>
  <form @submit.prevent="submit" autocomplete="off">
    <div v-if="createNew">
      <p class="mt-0">
        <span class="font-weight-bolder">{{ $t("ByPass OTG") }}</span
        >:
        {{
          $t(
            "Do not use OTG when at a particular IP and/or when accessing a particular domain."
          )
        }}
      </p>
      <p>
        <span class="font-weight-bolder">{{ $t("Use Specific Resolver") }}</span
        >:
        {{
          $t(
            "Use a specific resolver when at a particular IP and/or when accessing a particular domain."
          )
        }}
      </p>
      <hr />
    </div>
    <t-modal-section :title="$t('Device Exception Rule Details')" class="mb-3">
      <b-row>
        <b-col sm="12" md="12" lg="6">
          <b-form-group
            id="dropdown"
            :label="this.$t('Select rule type')"
            label-for="dropdown"
          >
            <b-form-select
              autofocus
              data-test-id="update-data-test-id"
              v-model="form.device_exception_rule_type_id"
              :options="ruleTypeOptions"
              :disabled="!createNew"
            ></b-form-select>
          </b-form-group>
          <b-form-group
            :label="$t('Description (optional)')"
            label-for="exception_description"
            :state="$validateState($v.form.description)"
          >
            <b-form-textarea
              id="exception_description"
              :state="$validateState($v.form.description)"
              @blur="setValidationInput('description', $event.target.value)"
              :placeholder="$t('Rule description')"
              :value="this.form.description"
              rows="3"
              max-rows="6"
            ></b-form-textarea>
          </b-form-group>
        </b-col>

        <b-col sm="12" md="12" lg="6">
          <!-- resolver -->
          <b-form-group
            v-if="form.device_exception_rule_type_id === 2"
            :label="$t('Resolver')"
            label-for="resolver"
            :state="$validateState($v.form.resolver)"
          >
            <b-form-input
              data-test-id="update-data-test-id"
              :state="$validateState($v.form.resolver)"
              id="resolver"
              @blur="setValidationInput('resolver', $event.target.value)"
              :placeholder="$t('For example, 198.51.100.0')"
              :value="this.form.resolver"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.resolver.required">
                {{ $t("This is a required field.") }}
              </p>
              <p
                v-if="
                  $v.form.resolver.$dirty &&
                  $v.form.resolver.required &&
                  !$v.form.resolver.containsSpecial
                "
              >
                {{
                  $t("X must be valid IP or CIDR", { field: $t("Resolver") })
                }}
              </p>
            </template>
          </b-form-group>

          <!-- ip -->
          <b-form-group
            :label="$t('Source IP')"
            label-for="ip"
            :state="$validateState($v.form.ip)"
          >
            <b-form-input
              data-test-id="update-data-test-id"
              :state="$validateState($v.form.ip)"
              id="ip"
              @blur="setValidationInput('ip', $event.target.value)"
              :placeholder="$t('For example, 203.0.113.0')"
              :value="this.form.ip"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.ip.required">
                {{
                  $t("X required unless X is provided", {
                    field: $t("Source IP"),
                    other_field: $t("Domain Name"),
                  })
                }}
              </p>
              <p
                v-if="
                  $v.form.ip.$dirty &&
                  form.ip.length > 0 &&
                  !$v.form.ip.containsSpecial
                "
              >
                {{
                  $t("X must be valid IP or CIDR", { field: $t("Source IP") })
                }}
              </p>
            </template>
          </b-form-group>

          <!-- domain -->
          <b-form-group
            :label="$t('Domain Name')"
            label-for="domain"
            :state="$validateState($v.form.domain)"
          >
            <b-form-input
              data-test-id="update-data-test-id"
              :state="$validateState($v.form.domain)"
              id="domain"
              @blur="setValidationInput('domain', $event.target.value)"
              :placeholder="$t('For example, www.example.com')"
              :value="this.form.domain"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.domain.required">
                {{
                  $t("X required unless X is provided", {
                    field: $t("Domain"),
                    other_field: $t("Source IP"),
                  })
                }}
              </p>
              <p
                v-if="
                  $v.form.domain.$dirty &&
                  form.domain.length > 0 &&
                  !$v.form.domain.isQualifiedDomain
                "
              >
                {{
                  $t(
                    "Please specify domain or partial domain only URLs are not permitted."
                  )
                }}
              </p>
            </template>
          </b-form-group>
        </b-col>
      </b-row>
    </t-modal-section>
  </form>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { requiredIf } from "vuelidate/lib/validators";

import TModalSection from "@/components/TModalSection";

export default {
  components: {
    TModalSection,
  },
  data() {
    return {
      ruleTypeOptions: [
        { value: 1, text: this.$t("ByPass OTG") },
        { value: 2, text: this.$t("Use Specific Resolver") },
      ],
      form: {
        domain: "",
        ip: "",
        resolver: "",
        device_exception_rule_type_id: 1,
        description: "",
      },
      createNew: false,
      exceptionId: null,
    };
  },
  validations: {
    form: {
      resolver: {
        required: requiredIf(function () {
          return this.form.device_exception_rule_type_id === 2;
        }),
        containsSpecial(ip) {
          return ip.length > 0 ? this.$cidrOrIP(ip) : true;
        },
      },
      ip: {
        required: requiredIf(function (form) {
          return form.domain.length === 0;
        }),
        containsSpecial(ip) {
          return ip.length > 0 ? this.$cidrOrIP(ip) : true;
        },
      },
      domain: {
        required: requiredIf(function (form) {
          return form.ip.length === 0;
        }),
        isQualifiedDomain(domain) {
          return domain.length > 0 ? this.$isQualifiedDomain(domain) : true;
        },
      },
      description: {},
    },
  },
  methods: {
    ...mapMutations("modal", ["userAction", "showModal"]),
    ...mapActions("deviceExceptions", [
      "createDeviceExceptionRule",
      "updateDeviceExceptionRule",
    ]),
    ...mapActions("toast", ["displayToast"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async submit() {
      let body = this.form;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        if (this.createNew) {
          // Creating device exception rule
          const response = await this.createDeviceExceptionRule({
            userId: this.selectedUser.id,
            exceptionId: this.exceptionId,
            body,
          });

          if (response.code === 200) {
            this.displayToast({
              title: this.$t("Success"),
              message: this.$t("Created"),
              duration: 2000,
              variant: "success",
            });
            this.showModal(false);
          }
        } else {
          // Updating device exception rule
          const response = await this.updateDeviceExceptionRule({
            id: body.id,
            userId: this.selectedUser.id,
            exceptionId: this.exceptionId,
            body,
          });
          this.$store.commit(
            "deviceExceptions/updateDeviceExceptionRule",
            response
          );
          this.$emit("force-update");
          if (response.code === 200) {
            this.displayToast({
              title: this.$t("Success"),
              message: this.$t("Updated"),
              duration: 2000,
              variant: "success",
            });
            this.showModal(false);
          }
        }
      }
    },
  },
  computed: {
    ...mapGetters("modal", ["modalData"]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  mounted() {
    this.createNew = this.modalData.createNew;
    this.exceptionId = this.modalData.exceptionId;
    if (this.modalData.item && !this.createNew) {
      this.modalData.item.device_exception_rule_type_id =
        this.ruleTypeOptions.find(
          (element) => element.text === this.modalData.item.rule_type
        ).value;
      // Turn the `null` objects to empty brackets
      let modalDataItem = this.modalData.item;
      this.form = Object.keys(modalDataItem).reduce((acc, key) => {
        acc[`${key}`] =
          modalDataItem[`${key}`] === null ? "" : modalDataItem[`${key}`];
        return acc;
      }, {});
    }
  },
  created() {
    this.userAction(this.submit);
  },
};
</script>
